// "Which company is this user in": the shared part of the apps'
// CheckInformation / CompanyWrapper. getUserCompany goes through the request
// cache (one request per token per page), and the company id/name and user id
// cookies the apps read are set on the parent domain.
//
// With both company cookies present the lookup is skipped -- except on a
// fresh login (?token=), which may be another user or company.

import { useCallback, useEffect, useRef, useState } from "react";
import { getSessionConfig, ResolvedSessionConfig } from "./config";
import { cookies } from "./cookies";
import { cachedRequest, CACHED } from "./requestCache";
import { useSession } from "./SessionProvider";

export type UserCompany = {
	companyID: string;
	name?: string;
	[field: string]: unknown;
};

export type CompanyBootstrapStatus = "loading" | "ready" | "noCompany" | "error";

export type CompanyBootstrap = {
	status: CompanyBootstrapStatus;
	/** the user's (first) company; from the cookies when the lookup was skipped */
	company: UserCompany | null;
	/** every company the lookup returned (empty when it was skipped) */
	companies: UserCompany[];
	/** the lookup found a different company than the cookies held (reload company-scoped data) */
	companyChanged: boolean;
	error: unknown;
	/** look the company up again, bypassing the cookies and the cache */
	reload: () => void;
};

export class CompanyLookupError extends Error {
	readonly status: number;
	constructor(status: number) {
		super(`getUserCompany failed: ${status}`);
		this.name = "CompanyLookupError";
		this.status = status;
	}
}

/**
 * GET {apiUrl}/company?type=getUserCompany through the request cache. Resolves
 * with the company list; rejects on a non-2xx answer or a network error.
 */
export function fetchUserCompanies(
	token: string,
	options: { fresh?: boolean; config?: ResolvedSessionConfig } = {}
): Promise<UserCompany[]> {
	const config = options.config ?? getSessionConfig();
	const url = `${config.apiUrl}${CACHED.userCompany}`;
	return cachedRequest(
		token,
		url,
		async () => {
			const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
			if (!res.ok) throw new CompanyLookupError(res.status);
			const data = await res.json();
			return Array.isArray(data) ? (data as UserCompany[]) : [];
		},
		{ fresh: options.fresh }
	);
}

/** Writes the company id/name cookies (parent domain). */
export function storeCompanyCookies(company: UserCompany, config: ResolvedSessionConfig = getSessionConfig()): void {
	const names = cookies.names(config);
	cookies.set(names.companyId, `${company.companyID}`, config);
	if (company.name !== undefined && company.name !== null) cookies.set(names.companyName, `${company.name}`, config);
}

export type CompanyBootstrapOptions = {
	/** false: don't look anything up yet (default true) */
	enabled?: boolean;
	/** always look the company up, even with both cookies present */
	always?: boolean;
};

export function useCompanyBootstrap(options: CompanyBootstrapOptions = {}): CompanyBootstrap {
	const { enabled = true, always = false } = options;
	const session = useSession();
	const { config, status: sessionStatus, freshLogin } = session;
	const sub = session.claims?.sub;
	const tokenRef = useRef(session.token);
	tokenRef.current = session.token;
	const [reloads, setReloads] = useState(0);
	const [state, setState] = useState<Omit<CompanyBootstrap, "reload">>({
		status: "loading",
		company: null,
		companies: [],
		companyChanged: false,
		error: null,
	});

	useEffect(() => {
		if (!enabled || sessionStatus !== "signedIn" || !tokenRef.current) return;
		const token = tokenRef.current;
		const names = cookies.names(config);
		if (sub && cookies.get(names.userId) !== sub) cookies.set(names.userId, sub, config);

		const knownId = cookies.get(names.companyId);
		const knownName = cookies.get(names.companyName);
		if (knownId && knownName && !freshLogin && !always && reloads === 0) {
			setState({
				status: "ready",
				company: { companyID: knownId, name: knownName },
				companies: [],
				companyChanged: false,
				error: null,
			});
			return;
		}

		let cancelled = false;
		setState((previous) => ({ ...previous, status: "loading", error: null }));
		fetchUserCompanies(token, { fresh: reloads > 0, config }).then(
			(companies) => {
				if (cancelled) return;
				const company = companies[0] ?? null;
				if (!company) {
					setState({ status: "noCompany", company: null, companies, companyChanged: false, error: null });
					return;
				}
				storeCompanyCookies(company, config);
				setState({
					status: "ready",
					company,
					companies,
					companyChanged: knownId !== undefined && knownId !== `${company.companyID}`,
					error: null,
				});
			},
			(error) => {
				if (cancelled) return;
				console.error(`[web-session:${config.app}] company lookup failed`, error?.message ?? error);
				setState({ status: "error", company: null, companies: [], companyChanged: false, error });
			}
		);
		return () => {
			cancelled = true;
		};
		// re-run for another user / a fresh login / reload(), not for a silently refreshed token
	}, [enabled, always, sessionStatus, freshLogin, sub, reloads, config]);

	const reload = useCallback(() => setReloads((n) => n + 1), []);
	return { ...state, reload };
}
